/**
 * Projects Module UI Slot Declarations
 *
 * This file defines the UI extension points for the projects module.
 * UI components are implemented in the ./svelte subpath.
 */

import type { ModuleUISlot, SmrtModuleMeta } from '@happyvertical/smrt-types';

/**
 * Projects module UI slots
 */
export const PROJECTS_UI_SLOTS: Record<string, ModuleUISlot> = {
  'development-request-form': {
    id: 'development-request-form',
    label: 'Development Request Form',
    description: 'Reusable managed-application request submission form',
    icon: 'send',
    category: 'form',
    order: 1,
    propsInterface: 'DevelopmentRequestFormProps',
  },
  'development-request-list': {
    id: 'development-request-list',
    label: 'Development Request List',
    description: 'Requester-facing list of managed development requests',
    icon: 'list',
    category: 'list',
    order: 2,
    propsInterface: 'DevelopmentRequestListProps',
  },
  'development-request-detail': {
    id: 'development-request-detail',
    label: 'Development Request Detail',
    description: 'Provider-neutral request detail and delivery timeline',
    icon: 'file-text',
    category: 'display',
    order: 3,
    propsInterface: 'DevelopmentRequestDetailProps',
  },
  'development-board': {
    id: 'development-board',
    label: 'Development Board',
    description: 'Visibility-filtered projection of connected delivery work',
    icon: 'columns',
    category: 'display',
    order: 4,
    propsInterface: 'DevelopmentBoardProps',
  },
  'project-board': {
    id: 'project-board',
    label: 'Project Board',
    description: 'Provider-backed project status board',
    icon: 'columns',
    category: 'display',
    order: 5,
    propsInterface: 'ProjectBoardProps',
  },
  'delivery-status': {
    id: 'delivery-status',
    label: 'Delivery Status',
    description: 'Ordered timeline of delivery outcomes',
    icon: 'activity',
    category: 'display',
    order: 6,
    propsInterface: 'DeliveryStatusProps',
  },
  'preview-approval': {
    id: 'preview-approval',
    label: 'Preview Approval',
    description: 'Approve or reject a managed delivery preview',
    icon: 'eye',
    category: 'action',
    order: 6,
    propsInterface: 'PreviewApprovalPanelProps',
  },
  'assistance-launcher': {
    id: 'assistance-launcher',
    label: 'Assistance Launcher',
    description: 'Open a contextual support or development conversation',
    icon: 'message-circle',
    category: 'form',
    order: 7,
    propsInterface: 'AssistanceLauncherProps',
  },
  'service-evidence-list': {
    id: 'service-evidence-list',
    label: 'Service Evidence List',
    description: 'Shared service duration, charge, and compensation evidence',
    icon: 'receipt',
    category: 'display',
    order: 8,
    propsInterface: 'ServiceEvidenceListProps',
  },
  'time-entry-card': {
    id: 'time-entry-card',
    label: 'Time Entry Card',
    description: 'Card component for displaying time entries',
    icon: 'clock',
    category: 'display',
    order: 3,
    propsInterface: 'TimeEntryCardProps',
  },
  'time-entry-list': {
    id: 'time-entry-list',
    label: 'Time Entry List',
    description: 'List of time entries with optional selection',
    icon: 'list',
    category: 'list',
    order: 4,
    propsInterface: 'TimeEntryListProps',
  },
  'time-summary': {
    id: 'time-summary',
    label: 'Time Summary',
    description: 'Summary statistics for time entries',
    icon: 'chart',
    category: 'display',
    order: 5,
    propsInterface: 'TimeSummaryProps',
  },
  'duration-display': {
    id: 'duration-display',
    label: 'Duration Display',
    description: 'Formats hours for display in decimal or HH:MM format',
    icon: 'timer',
    category: 'display',
    order: 6,
    propsInterface: 'DurationDisplayProps',
  },
  'approval-actions': {
    id: 'approval-actions',
    label: 'Approval Actions',
    description: 'Status-based action buttons for approval workflow',
    icon: 'check',
    category: 'action',
    order: 7,
    propsInterface: 'ApprovalActionsProps',
  },
  'bulk-actions': {
    id: 'bulk-actions',
    label: 'Bulk Actions',
    description: 'Action bar for bulk operations on selected items',
    icon: 'select-all',
    category: 'action',
    order: 8,
    propsInterface: 'BulkActionsProps',
  },
  'reject-dialog': {
    id: 'reject-dialog',
    label: 'Reject Dialog',
    description: 'Modal dialog for rejecting with a reason',
    icon: 'x-circle',
    category: 'form',
    order: 9,
    propsInterface: 'RejectDialogProps',
  },
};

/**
 * Projects module metadata
 */
export const PROJECTS_MODULE_META: SmrtModuleMeta = {
  name: '@happyvertical/smrt-projects',
  displayName: 'Projects',
  description:
    'Provider-agnostic project management with managed-app request intake and time tracking',
  uiSlots: PROJECTS_UI_SLOTS,
  models: [
    'Repository',
    'Issue',
    'PullRequest',
    'Project',
    'Comment',
    'Label',
    'ProjectIntegration',
    'ProjectIntegrationAudit',
    'DevelopmentRequest',
    'DevelopmentRequestHistory',
    'DevelopmentRequestWorkLink',
    'ProjectDeliveryEvent',
    'PreviewApproval',
    'AssistanceRequest',
    'AssistanceRequestEvent',
    'ServiceTimeEntry',
    'ServiceChargeSnapshot',
    'ServiceCompensationSnapshot',
  ],
  collections: [
    'RepositoryCollection',
    'IssueCollection',
    'PullRequestCollection',
    'ProjectCollection',
    'CommentCollection',
    'LabelCollection',
    'ProjectIntegrationCollection',
    'ProjectIntegrationAuditCollection',
    'DevelopmentRequestCollection',
    'DevelopmentRequestHistoryCollection',
    'DevelopmentRequestWorkLinkCollection',
    'ProjectDeliveryEventCollection',
    'PreviewApprovalCollection',
    'AssistanceRequestCollection',
    'AssistanceRequestEventCollection',
    'ServiceTimeEntryCollection',
    'ServiceChargeSnapshotCollection',
    'ServiceCompensationSnapshotCollection',
  ],
};
